import Header from '@/taskpane/components/Header';
import LetterGlitch from '@/taskpane/components/LetterGlitch';
import LoginForm from '@/taskpane/components/LoginForm';
import LogoutDialog from '@/taskpane/components/LogoutDialog';
import { useAuth } from '@/taskpane/hooks/useAuth';
import { useDocuments } from '@/taskpane/hooks/useDocuments';
import { useHostVars } from '@/taskpane/hooks/useHostVars';
import type { OfficeHost } from '@/taskpane/services/OfficeHostService';
import { lazy, Suspense, useCallback, useState } from 'react';

import type { Document } from './common/types';

const DocumentList = lazy(() => import('@/taskpane/components/DocumentList'));
const DebugPanel = lazy(() => import('@/taskpane/components/DebugPanel'));
const ProfilePage = lazy(() => import('@/taskpane/components/profile/ProfilePage'));

interface AppProperties {
  officeHost?: OfficeHost;
}

const App: React.FC<AppProperties> = ({ officeHost = 'Unknown' }) => {
  useHostVars(officeHost);

  const { isAuthenticated, isLoadingLogin, user, error, login, logout, setError } = useAuth();
  const {
    documents,
    isLoadingDocuments,
    isOpeningDocument,
    isClosingDocument,
    isSavingEdits,
    openDocumentId,
    loadDocuments,
    openDocument,
    closeDocument,
    shareDocument,
    saveEdits,
    saveAsNew,
    clearDocuments,
  } = useDocuments();

  const [currentPage, setCurrentPage] = useState<'documents' | 'profile'>('documents');
  const [debugPanelOpen, setDebugPanelOpen] = useState(false);
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);
  const [localError, setLocalError] = useState('');

  const displayError = error || localError;

  const handleLogin = useCallback(
    async (phoneNumber: string) => {
      await login(phoneNumber);
      if (!error) {
        try {
          await loadDocuments();
        } catch {
          setLocalError('Failed to load documents');
        }
      }
    },
    [login, loadDocuments, error],
  );

  const handleLogout = useCallback(async () => {
    setIsLogoutDialogOpen(false);
    await clearDocuments();
    await logout();
  }, [clearDocuments, logout]);

  const handleDocumentOpen = useCallback(
    async (document: Document) => {
      try {
        await openDocument(document);
      } catch {
        setLocalError('Failed to open document');
      }
    },
    [openDocument],
  );

  const handleDocumentClose = useCallback(
    async (documentId: string) => {
      try {
        await closeDocument(documentId);
      } catch {
        setLocalError('Failed to close document');
      }
    },
    [closeDocument],
  );

  const handleSaveEdits = useCallback(
    async (documentId: string) => {
      try {
        await saveEdits(documentId);
      } catch (error_) {
        setLocalError(error_ instanceof Error ? error_.message : 'Failed to save document edits');
      }
    },
    [saveEdits],
  );

  const handleSaveAsNew = useCallback(
    async (fileName: string, workspaceId?: string, folderId?: string) => {
      try {
        await saveAsNew(fileName, workspaceId, folderId);
      } catch (error_) {
        setLocalError(error_ instanceof Error ? error_.message : 'Failed to save new document');
      }
    },
    [saveAsNew],
  );

  return (
    <div
      className="app-container bg-background text-foreground relative mx-auto min-h-screen overflow-hidden"
      style={{ maxWidth: 600 }}
    >
      <div className="absolute inset-0 z-0">
        <LetterGlitch
          glitchColors={['#26251e', '#f54e00', '#5a5852', '#3a3832']}
          glitchSpeed={80}
          centerVignette={false}
          outerVignette={true}
          smooth={true}
        />
        <div className="from-background/95 via-background/70 to-background/95 absolute inset-0 bg-gradient-to-b" />
      </div>

      <div className="relative z-10">
        {isAuthenticated && (
          <Header
            user={user}
            onLogout={() => {
              setIsLogoutDialogOpen(true);
            }}
            onNavigateToProfile={() => {
              setCurrentPage('profile');
            }}
            officeHost={officeHost}
          />
        )}

        {displayError && (
          <div className="flex items-center justify-between bg-[#fbeff1] px-4 py-2 text-sm text-[#cf2d56]">
            <span>{displayError}</span>
            <button
              onClick={() => {
                setError('');
                setLocalError('');
              }}
              className="ml-2 cursor-pointer border-none bg-transparent p-1 text-lg text-[#cf2d56]"
            >
              ×
            </button>
          </div>
        )}

        <main className="app-main">
          {isAuthenticated ? (
            <>
              {currentPage === 'profile' ? (
                <Suspense fallback={null}>
                  <ProfilePage
                    onBack={() => {
                      setCurrentPage('documents');
                    }}
                  />
                </Suspense>
              ) : (
                <Suspense fallback={null}>
                  <DocumentList
                    documents={documents}
                    onDocumentOpen={handleDocumentOpen}
                    onDocumentShare={shareDocument}
                    onCloseDocument={handleDocumentClose}
                    onSaveAsNew={handleSaveAsNew}
                    onSaveEdits={handleSaveEdits}
                    isLoadingDocuments={isLoadingDocuments}
                    isSavingEdits={isSavingEdits}
                    openingDocumentId={isOpeningDocument}
                    closingDocumentId={isClosingDocument}
                    openDocumentId={openDocumentId}
                    onReload={loadDocuments}
                  />
                </Suspense>
              )}

              <LogoutDialog
                isOpen={isLogoutDialogOpen}
                onOpenChange={(open) => {
                  if (!open) setIsLogoutDialogOpen(false);
                }}
                onConfirm={() => void handleLogout()}
              />
            </>
          ) : (
            <LoginForm onLogin={handleLogin} isLoading={isLoadingLogin} />
          )}
        </main>

        {debugPanelOpen && (
          <Suspense fallback={null}>
            <DebugPanel
              isOpen={debugPanelOpen}
              onClose={() => {
                setDebugPanelOpen(false);
              }}
            />
          </Suspense>
        )}
      </div>
    </div>
  );
};

export default App;
